import type { FC } from 'react';
import React from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import {
	EDIT_PAGE_V2,
	SPACE_OVERVIEW,
	VIEW_PAGE,
} from '@confluence/named-routes/entry-points/routes';
import { NotFound } from '@confluence/not-found/entry-points/NotFound';
import { OverviewNotFound } from '@confluence/not-found/entry-points/OverviewNotFound';
import type { Route } from '@confluence/route/entry-points/Route';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection';

import type {
	NotFoundContainerQuery$data,
	NotFoundContainerQuery$variables,
} from './__generated__/NotFoundContainerQuery.graphql';
import { ContentExpansionContainer } from './styles';

export type NotFoundContainerProps = {
	route: Route;
	spaceKey?: string;
	contentId?: string;
};

export const NotFoundContainer: FC<NotFoundContainerProps> = ({ route, spaceKey, contentId }) => {
	// View page URLs inserted by the `/Create Confluence page` extension will include draftShareId
	const urlDraftShareId = window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '';

	/*
	 * If we've hit this component, the page may actually be an unpublished draft,
	 * if so then redirect to the editor given the user has edit permission
	 */
	const { data } = useQueryInterop<NotFoundContainerQuery$data, NotFoundContainerQuery$variables>(
		graphql`
			query NotFoundContainerQuery($contentId: ID!, $draftShareId: String, $status: [String]) {
				content(id: $contentId, draftShareId: $draftShareId, status: $status) {
					nodes {
						id
						links {
							webui
						}
						properties(key: "editor") {
							nodes {
								id
								key
								value
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: contentId ?? '',
				draftShareId: urlDraftShareId,
				status: ['draft'],
			},
			skip: !contentId || !spaceKey || route !== VIEW_PAGE,
			fetchPolicy: window.__SSR_RENDERED__ ? 'cache-only' : 'cache-and-network',
			onError: (error) => {
				if (!isUnauthorizedError(error) && !isNotFoundError(error)) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXPERIENCES,
					});
				}
				markErrorAsHandled(error);
			},
		},
	);

	// Query result will include draftShareId if the page is a draft
	const draftShareId =
		data?.content?.nodes?.[0]?.links?.webui?.match(/draftShareId=(.*?)(#|&|$)/)?.[1];
	const isFabric = data?.content?.nodes?.[0]?.properties?.nodes?.[0]?.value === '"v2"';

	if (isFabric && draftShareId) {
		return (
			<Redirection
				href={EDIT_PAGE_V2.toUrl(
					{
						contentId,
						spaceKey,
						contentType: 'pages',
					},
					{
						query: {
							draftShareId,
						},
					},
				)}
			/>
		);
	}

	return (
		<ContentExpansionContainer>
			<DocumentContentTitle notFound />
			{route === SPACE_OVERVIEW ? <OverviewNotFound spaceKey={spaceKey} /> : <NotFound />}
		</ContentExpansionContainer>
	);
};
